const BUSY_TABS_KEY = 'rm_busy_tabs';
const BUSY_ENTRY_TTL_MS = 10000;
const BUSY_HEARTBEAT_MS = 4000;

const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

type BusyTabEntry = {
  expiresAt: number;
  operations: string[];
};

type BusyTabsMap = { [tabId: string]: BusyTabEntry };

function readBusyTabs(): BusyTabsMap {
  try {
    const raw = localStorage.getItem(BUSY_TABS_KEY);
    return raw ? (JSON.parse(raw) as BusyTabsMap) : {};
  } catch {
    return {};
  }
}

function writeBusyTabs(map: BusyTabsMap): void {
  if (Object.keys(map).length === 0) {
    localStorage.removeItem(BUSY_TABS_KEY);
  } else {
    localStorage.setItem(BUSY_TABS_KEY, JSON.stringify(map));
  }
}

function withoutExpired(map: BusyTabsMap): BusyTabsMap {
  const now = Date.now();
  return Object.fromEntries(
    Object.entries(map).filter(([, entry]) => entry?.expiresAt > now),
  );
}

let runningTaskLabels: string[] = [];
const pendingApiCalls = new Map<string, number>();
let heartbeat: ReturnType<typeof setInterval> | null = null;

function currentOperations(): string[] {
  const apiOperations = Array.from(pendingApiCalls.entries()).map(([url, count]) =>
    count > 1 ? `${url} (x${count})` : url,
  );
  return [...runningTaskLabels, ...apiOperations];
}

function updateOwnEntry(): void {
  const operations = currentOperations();
  const map = withoutExpired(readBusyTabs());

  if (operations.length === 0) {
    if (heartbeat != null) {
      clearInterval(heartbeat);
      heartbeat = null;
    }
    delete map[TAB_ID];
  } else {
    map[TAB_ID] = { expiresAt: Date.now() + BUSY_ENTRY_TTL_MS, operations };
    if (heartbeat == null) {
      heartbeat = setInterval(updateOwnEntry, BUSY_HEARTBEAT_MS);
    }
  }

  writeBusyTabs(map);
}

function removeOwnEntry(): void {
  const map = withoutExpired(readBusyTabs());
  delete map[TAB_ID];
  writeBusyTabs(map);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', removeOwnEntry);
}

export function setTabRunningTasks(labels: string[]): void {
  runningTaskLabels = labels;
  updateOwnEntry();
}

export function apiCallStarted(url: string): void {
  pendingApiCalls.set(url, (pendingApiCalls.get(url) ?? 0) + 1);
  updateOwnEntry();
}

export function apiCallFinished(url: string): void {
  const count = (pendingApiCalls.get(url) ?? 0) - 1;
  if (count > 0) {
    pendingApiCalls.set(url, count);
  } else {
    pendingApiCalls.delete(url);
  }
  updateOwnEntry();
}

export function isAnyTabBusy(): boolean {
  if (currentOperations().length > 0) {
    return true;
  }
  return Object.keys(withoutExpired(readBusyTabs())).length > 0;
}
